import { Form } from 'informed';
import { map, prop, uniq } from 'ramda';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProfileSelectDate } from '@/components/form/ProfileDateSelect';
import { ProfileSelect } from '@/components/form/Select';
import {
  currencyFlags,
  mapCurrencyName,
  mapCurrencySymbol,
} from '@/helpers/currency';
import { getBonusStatusNameTranslation } from '@/modules/promotions/helpers';
import { useCommonStore } from '@/store';

import { ScBonusesFiltration } from './styled';

interface BonusesFiltrationProps {
  setCurrency: (value: string) => void;
  setStatus: (value: string) => void;
  setDate: (value: [Date | null, Date | null]) => void;
}

export const BonusesFiltration: FC<BonusesFiltrationProps> = ({
  setCurrency,
  setDate,
  setStatus,
}) => {
  const { t, i18n } = useTranslation('bonuses');

  const bonuses = useCommonStore.use.bonuses();
  const freespins = useCommonStore.use.freespins();

  const currencyOptions = useMemo(() => {
    const userBonusStages = uniq(
      map(prop('currency'), [...bonuses, ...freespins])
    );

    return userBonusStages.map((code) => {
      const mask = mapCurrencySymbol[code];
      const name = mapCurrencyName[code];

      const currencySymbol = mask ? mask.replace('{}', '') : code;

      return {
        name: `${currencySymbol}${name ? ` | ${name}` : ''}`,
        value: code,
        icon: currencyFlags[code],
      };
    });
  }, [bonuses, freespins]);

  const stagesSelectOptions = useMemo(() => {
    const userBonusStages = uniq(
      map(prop('stage'), [...bonuses, ...freespins])
    );

    return userBonusStages.map((stage) => {
      const statusTranslation = getBonusStatusNameTranslation(
        t,
        i18n,
        stage,
        true
      );

      return {
        name: statusTranslation,
        value: stage,
      };
    });
  }, [bonuses, freespins, i18n, t]);

  return (
    <ScBonusesFiltration>
      <Form>
        <div>
          <ProfileSelect
            name="currency"
            options={currencyOptions}
            placeholder={t('currency')}
            onSelectOption={(value) => setCurrency(value.toString())}
            canReset
          />
          <ProfileSelect
            name="status"
            options={stagesSelectOptions}
            placeholder={t('status')}
            onSelectOption={(value) => setStatus(value.toString())}
            canReset
          />
          <ProfileSelectDate
            name="date"
            onSelectDateRange={(value) => setDate(value)}
            canReset
          />
        </div>
      </Form>
    </ScBonusesFiltration>
  );
};
